// analytics/components/PageTracker.js
import log from 'loglevel';
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAnalytics } from 'analytics/hooks/useAnalytics';

const PageTracker = ({ 
  enableScrollTracking = false,
  enablePerformanceTracking = true,
  enableVisibilityTracking = true,
  enableErrorBoundary = true,
  customEventHandlers = {},
  debugMode = false,
}) => {
  const location = useLocation();
  const { token, mode } = useSelector(state => state.userInfo || {});
  
  const {
    trackPage,
    trackRouteChange,
    trackEvent,
    trackError,
    updateCurrentPageTime,
    debouncedTimeUpdate,
    throttledScrollTrack,
    markPerformance,
    measurePerformance,
    initialize,
    cleanup,
    isReady,
    initialized,
  } = useAnalytics();

  // Refs for tracking state
  const isInitialMount = useRef(true);
  const previousLocation = useRef(location);
  const previousToken = useRef(token);
  const previousMode = useRef(mode);
  const pageLoadStartTime = useRef(null);
  const visibilityStartTime = useRef(Date.now());
  const scrollDepthMarks = useRef(new Set());

  // Debug logging helper
  const debugLog = useCallback((message, data = {}) => {
    if (debugMode) {
      log.log(`PageTracker: ${message}`, data);
    }
  }, [debugMode]);

  // ====== PERFORMANCE TRACKING ======

  useEffect(() => {
    if (enablePerformanceTracking) {
      pageLoadStartTime.current = performance.now();
      markPerformance('page_start');
      
      // Track page load performance
      const handleLoad = () => {
        markPerformance('page_loaded');
        measurePerformance('page_start', 'page_loaded', {
          event_type: 'page_load_performance',
          page_url: window.location.href,
        });
      };

      if (document.readyState === 'complete') {
        handleLoad();
      } else {
        window.addEventListener('load', handleLoad, { once: true });
        return () => window.removeEventListener('load', handleLoad);
      }
    }
  }, [enablePerformanceTracking, markPerformance, measurePerformance]);

  // ====== SCROLL DEPTH TRACKING ======

  useEffect(() => {
    if (!enableScrollTracking) return;

    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );

      // Track scroll depth milestones
      const milestones = [25, 50, 75, 90, 100];
      milestones.forEach(milestone => {
        if (scrollPercent >= milestone && !scrollDepthMarks.current.has(milestone)) {
          scrollDepthMarks.current.add(milestone);
          trackEvent('scroll_depth', {
            depth_percent: milestone,
            page_url: window.location.href,
          });
          debugLog(`Scroll depth milestone: ${milestone}%`);
        }
      });

      // Throttled general scroll tracking
      throttledScrollTrack();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      scrollDepthMarks.current.clear();
    };
  }, [enableScrollTracking, trackEvent, throttledScrollTrack, debugLog]);

  // ====== VISIBILITY TRACKING ======

  useEffect(() => {
    if (!enableVisibilityTracking) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page became hidden
        const visibleTime = Date.now() - visibilityStartTime.current;
        trackEvent('page_visibility_hidden', {
          visible_duration: Math.floor(visibleTime / 1000),
          page_url: window.location.href,
        });
        updateCurrentPageTime();
        debugLog('Page hidden', { visibleTime });
      } else {
        // Page became visible
        visibilityStartTime.current = Date.now();
        trackEvent('page_visibility_visible', {
          page_url: window.location.href,
        });
        debugLog('Page visible');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enableVisibilityTracking, trackEvent, updateCurrentPageTime, debugLog]);

  // ====== PAGE UNLOAD TRACKING ======

  useEffect(() => {
    const handleBeforeUnload = () => {
      updateCurrentPageTime();
      trackEvent('page_beforeunload', {
        page_url: window.location.href,
        session_duration: Date.now() - visibilityStartTime.current,
      });
      debugLog('Page beforeunload');
    };

    const handleUnload = () => {
      cleanup();
      debugLog('Page unload');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      // Final cleanup
      updateCurrentPageTime();
    };
  }, [updateCurrentPageTime, trackEvent, cleanup, debugLog]);

  // ====== ERROR BOUNDARY TRACKING ======

  useEffect(() => {
    if (!enableErrorBoundary) return;

    const handleError = (event) => {
      trackError(event.error || new Error(event.message), {
        error_type: 'unhandled_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        page_url: window.location.href,
      });
      debugLog('Unhandled error', event);
    };

    const handleRejection = (event) => {
      trackError(event.reason || new Error('Unhandled promise rejection'), {
        error_type: 'unhandled_rejection',
        page_url: window.location.href,
      });
      debugLog('Unhandled promise rejection', event);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [enableErrorBoundary, trackError, debugLog]);

  // ====== INITIALIZATION ======

  useEffect(() => {
    if (!initialized) {
      initialize({
        page_tracker_enabled: true,
        initial_page: window.location.href,
        user_mode: mode,
        features: {
          scroll_tracking: enableScrollTracking,
          performance_tracking: enablePerformanceTracking,
          visibility_tracking: enableVisibilityTracking,
          error_boundary: enableErrorBoundary,
        },
      });
      debugLog('Analytics initialized');
    }
  }, [initialized, initialize, mode, enableScrollTracking, enablePerformanceTracking, enableVisibilityTracking, enableErrorBoundary, debugLog]);

  // ====== MAIN TRACKING LOGIC ======

  useEffect(() => {
    if (!isReady()) {
      debugLog('Analytics not ready, skipping tracking');
      return;
    }

    const handleTracking = async () => {
      try {
        // Determine event type and data
        let eventType = 'page_view';
        let eventData = {};

        if (isInitialMount.current) {
          // Initial page load
          eventType = 'initial_page_load';
          eventData = {
            is_initial_load: true,
            user_mode: mode,
            load_timestamp: new Date().toISOString(),
          };
          debugLog('Tracking initial page load');
        } else {
          // Check what changed
          const locationChanged = (
            previousLocation.current.pathname !== location.pathname ||
            previousLocation.current.search !== location.search
          );
          const tokenChanged = previousToken.current !== token;
          const modeChanged = previousMode.current !== mode;

          if (locationChanged) {
            // Route change
            eventType = 'route_change';
            eventData = {
              previous_path: previousLocation.current.pathname,
              previous_search: previousLocation.current.search,
              new_path: location.pathname,
              new_search: location.search,
              user_mode: mode,
            };
            debugLog('Tracking route change', eventData);
            
            // Also call specialized route tracking
            trackRouteChange(eventData);
          } else if (tokenChanged) {
            eventType = 'token_change';
            eventData = {
              had_previous_token: !!previousToken.current,
              has_new_token: !!token,
              user_mode: mode,
            };
            debugLog('Tracking token change', eventData);
          } else if (modeChanged) {
            eventType = 'mode_change';
            eventData = {
              previous_mode: previousMode.current,
              new_mode: mode,
            };
            debugLog('Tracking mode change', eventData);
          } else {
            // No significant change
            debugLog('No significant change detected, skipping tracking');
            return;
          }
        }

        // Update refs before tracking
        previousLocation.current = location;
        previousToken.current = token;
        previousMode.current = mode;

        // Track the page/event
        await trackPage({
          event_type: eventType,
          ...eventData,
          ...customEventHandlers,
        });

        // Mark as no longer initial mount
        if (isInitialMount.current) {
          isInitialMount.current = false;
          debugLog('Initial mount completed');
        }

      } catch (error) {
        log.error('PageTracker: Tracking failed', error);
        if (enableErrorBoundary) {
          trackError(error, { context: 'page_tracker_tracking' });
        }
      }
    };

    handleTracking();
  }, [
    location.pathname, 
    location.search, 
    token, 
    mode, 
    trackPage, 
    trackRouteChange, 
    trackError,
    isReady,
    customEventHandlers,
    enableErrorBoundary,
    debugLog
  ]);

  // ====== RENDER ======

  // This component doesn't render anything
  return null;
};

export default PageTracker;